// Reading a .zip in the browser, so a zip of pictures can be uploaded without unzipping it first. Only the central
// directory is read up front; each file is sliced out and inflated when it is needed, so a zip of a few GB never
// sits in memory whole. Stored and deflated entries (what every common zip tool writes) are supported, ZIP64 too.
// Inflating uses the browser's own DecompressionStream("deflate-raw"); no library.

export interface ZipEntry {
  /** The path inside the zip, "/"-separated, as the zip names it. */
  path: string;
  size: number;
  compressedSize: number;
  /** 0 stored, 8 deflate; anything else cannot be read here. */
  method: number;
  encrypted: boolean;
  /** Where the entry's local header starts. */
  offset: number;
}

/** The file is not a zip, or not one this reader can open. The message is for a person. */
export class ZipError extends Error {}

const EOCD = 0x06054b50, EOCD64 = 0x06064b50, EOCD64_LOCATOR = 0x07064b50, CENTRAL = 0x02014b50, LOCAL = 0x04034b50;
const MAX_COMMENT = 0xffff;

const bytes = async (blob: Blob, start: number, end: number) => new DataView(await blob.slice(start, end).arrayBuffer());
const u64 = (v: DataView, at: number) => Number(v.getBigUint64(at, true));

function decodeName(raw: Uint8Array, utf8Flag: boolean): string {
  // Bit 11 says UTF-8. Without it the name is in the zip tool's code page; most are ASCII or UTF-8 anyway (macOS,
  // Linux), and Windows-1252 is the closest a browser decoder offers to the legacy CP437 for the rest.
  if (!utf8Flag) {
    try { return new TextDecoder("utf-8", { fatal: true }).decode(raw); } catch { return new TextDecoder("windows-1252").decode(raw); }
  }
  return new TextDecoder("utf-8").decode(raw);
}

/** The zip's file entries (directories left out), in the order the zip lists them. */
export async function readZip(blob: Blob): Promise<ZipEntry[]> {
  const tailStart = Math.max(0, blob.size - (22 + MAX_COMMENT));
  const tail = await bytes(blob, tailStart, blob.size);
  let at = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) if (tail.getUint32(i, true) === EOCD) { at = i; break; }
  if (at < 0) throw new ZipError("This file is not a zip, or it is damaged (no zip directory at its end).");

  let count = tail.getUint16(at + 10, true);
  let cdSize = tail.getUint32(at + 12, true);
  let cdOffset = tail.getUint32(at + 16, true);
  if (count === 0xffff || cdSize === 0xffffffff || cdOffset === 0xffffffff) {
    // ZIP64: the locator sits just before the end record and points at the 64-bit end record.
    const loc = at - 20;
    if (loc < 0 || tail.getUint32(loc, true) !== EOCD64_LOCATOR) throw new ZipError("This zip is damaged (its ZIP64 directory is missing).");
    const rec = u64(tail, loc + 8);
    const v = await bytes(blob, rec, rec + 56);
    if (v.getUint32(0, true) !== EOCD64) throw new ZipError("This zip is damaged (its ZIP64 directory is missing).");
    count = u64(v, 32); cdSize = u64(v, 40); cdOffset = u64(v, 48);
  }
  if (cdOffset + cdSize > blob.size) throw new ZipError("This zip is incomplete: it is shorter than its directory says. Download it again.");

  const cd = await bytes(blob, cdOffset, cdOffset + cdSize);
  const out: ZipEntry[] = [];
  let p = 0;
  for (let n = 0; n < count; n++) {
    if (p + 46 > cd.byteLength || cd.getUint32(p, true) !== CENTRAL) throw new ZipError("This zip is damaged (its directory cannot be read).");
    const flags = cd.getUint16(p + 8, true);
    const method = cd.getUint16(p + 10, true);
    let compressedSize = cd.getUint32(p + 20, true);
    let size = cd.getUint32(p + 24, true);
    const nameLen = cd.getUint16(p + 28, true), extraLen = cd.getUint16(p + 30, true), commentLen = cd.getUint16(p + 32, true);
    let offset = cd.getUint32(p + 42, true);
    const name = decodeName(new Uint8Array(cd.buffer, cd.byteOffset + p + 46, nameLen), (flags & 0x800) !== 0);
    // ZIP64 extra field: the 64-bit values of exactly those fields that are 0xFFFFFFFF above, in this order.
    for (let e = p + 46 + nameLen, end = e + extraLen; e + 4 <= end;) {
      const id = cd.getUint16(e, true), len = cd.getUint16(e + 2, true);
      if (id === 0x0001) {
        let q = e + 4;
        if (size === 0xffffffff) { size = u64(cd, q); q += 8; }
        if (compressedSize === 0xffffffff) { compressedSize = u64(cd, q); q += 8; }
        if (offset === 0xffffffff) { offset = u64(cd, q); }
      }
      e += 4 + len;
    }
    p += 46 + nameLen + extraLen + commentLen;
    const path = name.replace(/\\/g, "/");
    if (path.endsWith("/")) continue;      // a directory
    out.push({ path, size, compressedSize, method, encrypted: (flags & 1) !== 0, offset });
  }
  return out;
}

/** Why an entry cannot be read here, or null when it can. */
export function unreadable(e: ZipEntry): string | null {
  if (e.encrypted) return "password-protected";
  if (e.method !== 0 && e.method !== 8) return "compressed with a method the browser cannot unpack";
  return null;
}

/** One entry's contents. Throws ZipError when it cannot be read. */
export async function extract(blob: Blob, e: ZipEntry, type = ""): Promise<Blob> {
  const why = unreadable(e);
  if (why) throw new ZipError(`${e.path} is ${why}.`);
  const head = await bytes(blob, e.offset, e.offset + 30);
  if (head.byteLength < 30 || head.getUint32(0, true) !== LOCAL) throw new ZipError(`${e.path} cannot be found in the zip; it may be damaged.`);
  // The local header's name and extra field can differ in length from the directory's, so they are read from here.
  const start = e.offset + 30 + head.getUint16(26, true) + head.getUint16(28, true);
  const data = blob.slice(start, start + e.compressedSize);
  if (e.method === 0) return new Blob([data], { type });
  try {
    const out = await new Response(data.stream().pipeThrough(new DecompressionStream("deflate-raw"))).blob();
    if (out.size !== e.size) throw new Error("size");
    return new Blob([out], { type });
  } catch {
    throw new ZipError(`${e.path} is damaged and could not be unpacked.`);
  }
}
